/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventDetailBody.tsx
import css from '@emotion/css/macro'
import type { SnEvent } from '@sportninja/common/features/event/types'
import { formatEventLocation } from '@sportninja/common/features/event/formatEventLocation'
import EventDescriptionColumn from './EventDescriptionColumn'
import EventDetailsColumn from './EventDetailsColumn'
import EventLocationCard from './EventLocationCard'

interface EventDetailBodyProps {
  event: SnEvent
}

/**
 * Two-column detail layout.
 *
 * With a location: description + details stack on the LEFT, location card on
 * the RIGHT. Without one there is nothing to put on the right, so details moves
 * over rather than leaving a gap beside a lone description.
 */
const EventDetailBody: React.FC<EventDetailBodyProps> = ({ event }) => {
  // Covers the venue/facility/address triple — any of them makes a location
  // card worth showing. Coordinates are not required; the card falls back to a
  // plain address row without a map.
  const hasLocation = Boolean(formatEventLocation(event))

  return (
    <div css={bodyStyle}>
      <div css={columnStyle}>
        <EventDescriptionColumn description={event.details} />
        {hasLocation && <EventDetailsColumn event={event} />}
      </div>

      <div css={columnStyle}>
        {hasLocation ? (
          <EventLocationCard event={event} />
        ) : (
          <EventDetailsColumn event={event} />
        )}
      </div>
    </div>
  )
}

export default EventDetailBody

// ---- styled ----
/* Matches the container `Page` gives its own tab content (see
   components/Page/index.jsx) — 88px gutters, 1600px cap — so this page lines
   up with team/game/org screens instead of running to the viewport edge.
   No top margin: `Page`'s own content wrapper renders empty above this body
   (the detail layout is a sibling, not a tab), and its default 40px top margin
   was pure dead space — `Page` now zeroes it for `isEvent`. The 24px padding
   here is the only gap between header and content. */
const bodyStyle = css`
  display: grid;
  /* Near-even split. The left column now carries description AND details, so
     giving it a little more room keeps both readable without starving the
     location card. */
  grid-template-columns: minmax(0, 1.2fr) minmax(340px, 1fr);
  gap: 32px;
  margin: 0 auto 40px;
  padding: 24px 88px 0;
  max-width: 1600px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media (max-width: 768px) {
    margin: 0 auto 24px;
    padding: 16px 16px 0;
  }
`

/* Stacks the cards within a column and keeps the gap between them equal to the
   grid's own column gap, so the page reads as one consistent rhythm. */
const columnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-width: 0;

  @media (max-width: 1100px) {
    gap: 24px;
  }
`
